import type { Message } from '../types';
import { findDeliveredVersion, getRepairCodeVersions, looksLikePlcCode } from './messageTopic';

export interface PreviousGeneration {
  versionLabel: string;
  code: string;
}

/**
 * The program a turn delivered, and the version it called it.
 *
 * Read from the repair chain rather than from `parsed.code` alone, so a turn
 * whose program was repaired reports the version the user was actually left
 * with — the applied one — instead of the base it started from.
 */
function deliveredProgram(message: Message): PreviousGeneration | undefined {
  if (message.role !== 'assistant' || message.offTopic) return undefined;

  const versions = getRepairCodeVersions(message.repairAppendix, message.parsed?.code);
  const delivered = findDeliveredVersion(versions);
  const code = delivered?.code || message.parsed?.code || '';
  if (!looksLikePlcCode(code)) return undefined;

  return {
    versionLabel: delivered?.label || 'V1.0',
    code,
  };
}

/**
 * The program this turn supersedes, if an earlier turn delivered one.
 *
 * Version continuation is decided by the runtime — it stamps the version into
 * the program header and reads it back on the next turn — so nothing here
 * numbers anything. This only answers "what was the engineer looking at before
 * this turn replaced it", which is what the change summary and the diff need
 * and which no single message can know on its own.
 *
 * The list passed in is the branch the user is reading, so a turn on an
 * abandoned edit branch never becomes the baseline for the one on screen.
 */
export function findPreviousGeneration(
  messages: Message[],
  messageIndex: number,
): PreviousGeneration | undefined {
  for (let index = Math.min(messageIndex, messages.length) - 1; index >= 0; index -= 1) {
    const previous = deliveredProgram(messages[index]);
    if (previous) return previous;
  }
  return undefined;
}
